import DateFnsUtils from "@date-io/date-fns";
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ClearIcon from "@material-ui/icons/Clear";
import DownloadIcon from "@material-ui/icons/GetApp";
import { Autocomplete } from "@material-ui/lab";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import React from "react";
import { useTranslation } from "react-i18next";
import { colors } from "../../colors";
import { useFdaService } from "../../api/ServiceContext";
import { useSelectedMachine } from "../../store/GlobalContext";
import { useDispatchAlert } from "../../components/UI/Alert";

const useStyles = makeStyles((theme) => ({
  content: {
    padding: theme.spacing(2),
  },
  caption: {
    marginBottom: theme.spacing(2),
  },
  header: {
    fontWeight: "bold",
  },
  overviewSection: {
    padding: theme.spacing(2),
    "& ~ $overviewSection": {
      marginTop: theme.spacing(2),
    },
  },
  productSelectAutocomplete: {
    marginBottom: theme.spacing(1),
    width: 360,
    boxSizing: "border-box",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  supplySelectAutocomplete: {
    marginBottom: theme.spacing(4),
    width: 360,
    boxSizing: "border-box",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  downloadButton: {
    backgroundColor: colors.mobaBlue,
    color: "#fff",
  },
  datePicker: {
    marginBottom: theme.spacing(4),
    width: 360,
    boxSizing: "border-box",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  orText: {
    marginBottom: theme.spacing(1),
  },
}));

const FullWidthTextField = (props: React.ComponentProps<typeof TextField>) => (
  <TextField {...props} fullWidth />
);

function disableDownloadButton(
  startDate: Date | null,
  endDate: Date | null,
  product?: string | null,
  supply?: string | null,
) {
  return !(startDate && endDate && (product || supply));
}

export function PerformanceProFdaExport() {
  const { t } = useTranslation();
  const classes = useStyles();
  const service = useFdaService();
  const selectedMachineId = useSelectedMachine().machineId;
  const dispatchAlert = useDispatchAlert();

  const [isPageLoading, setIsPageLoading] = React.useState<boolean>(false);

  const [startDate, setStartDate] = React.useState<Date | null>(null);
  const [endDate, setEndDate] = React.useState<Date | null>(null);
  const earliestDate = new Date(new Date().getFullYear() - 1, 0, 1);

  const [product, setProduct] = React.useState<string | null>(null);
  const [supply, setSupply] = React.useState<string | null>(null);

  const [products, setProducts] = React.useState<string[]>([]);
  const [supplies, setSupplies] = React.useState<string[]>([]);

  function getProductsAndSupplies(startDate: Date, endDate: Date) {
    service
      .getProducts(selectedMachineId, startDate, endDate)
      .then((response: any) => {
        setProducts(response.data);
      });

    service
      .getSupplies(selectedMachineId, startDate, endDate)
      .then((response: any) => {
        setSupplies(response.data);
      });
  }

  function handleStartDateChange(date: Date | null) {
    setStartDate(date);
    if (date && endDate) {
      getProductsAndSupplies(date, endDate);
    }
  }

  function handleEndDateChange(date: Date | null) {
    setEndDate(date);
    if (startDate && date) {
      getProductsAndSupplies(startDate, date);
    }
  }

  async function handleDownload() {
    setIsPageLoading(true);
    try {
      await service.getFdaExport(selectedMachineId, {
        startDate: startDate!,
        endDate: endDate!,
        product: product,
        supply: supply,
      });
      dispatchAlert({
        message: "Successfully exported",
        messageType: "success",
      });
    } catch {
      dispatchAlert({
        message: "Error exporting",
        messageType: "error",
      });
    } finally {
      setIsPageLoading(false);
    }
  }

  return (
    <>
      <div className={classes.content}>
        {isPageLoading && <LinearProgress />}
        <Paper className={classes.overviewSection}>
          <Typography variant="h5" className={classes.header}>
            {t("PerformanceProFDAExportPage.header")}
          </Typography>
          <Grid container>
            <Grid container item xs={12} md={6}>
              <Grid item xs={12} className={classes.orText}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    className={classes.datePicker}
                    disableFuture
                    variant="inline"
                    label={t("PerformanceProFDAExportPage.startDateLabel")}
                    value={startDate}
                    onChange={(newStartDate) =>
                      handleStartDateChange(newStartDate)
                    }
                    minDate={
                      endDate
                        ? Math.max(
                            new Date(endDate).setDate(endDate.getDate() - 14),
                            earliestDate.getTime(),
                          )
                        : earliestDate
                    }
                    minDateMessage={t(
                      "PerformanceProFDAExportPage.startDateMinDateMessage",
                    )}
                    maxDate={endDate}
                    maxDateMessage={t(
                      "PerformanceProFDAExportPage.startDateMaxDateMessage",
                    )}
                    autoOk
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="end"
                          style={{ order: 1, marginRight: -2, paddingRight: 0 }}
                        >
                          <IconButton
                            onClick={() => setStartDate(null)}
                            disabled={!startDate}
                            aria-label={t(
                              "PerformanceProFDAExportPage.clearStartDate",
                            )}
                            aria-hidden={!startDate}
                            tabIndex={startDate ? 0 : -1}
                            style={{
                              opacity: startDate ? 1 : 0,
                              pointerEvents: startDate ? "auto" : "none",
                              padding: 6,
                              marginRight: -6,
                            }}
                          >
                            <ClearIcon color="disabled" fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    InputAdornmentProps={{
                      position: "end",
                      style: { order: 2, marginLeft: -2 },
                    }}
                    TextFieldComponent={FullWidthTextField}
                  />
                </MuiPickersUtilsProvider>
              </Grid>

              <Grid item xs={12}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    className={classes.datePicker}
                    disableFuture
                    variant="inline"
                    label={t("PerformanceProFDAExportPage.endDateLabel")}
                    value={endDate}
                    onChange={(newEndDate) => handleEndDateChange(newEndDate)}
                    minDate={startDate ?? earliestDate}
                    minDateMessage={t(
                      "PerformanceProFDAExportPage.endDateMinDateMessage",
                    )}
                    maxDate={
                      startDate
                        ? new Date(startDate).setDate(startDate.getDate() + 14)
                        : new Date()
                    }
                    maxDateMessage={t(
                      "PerformanceProFDAExportPage.endDateMaxDateMessage",
                    )}
                    autoOk
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="end"
                          style={{ order: 1, marginRight: -2, paddingRight: 0 }}
                        >
                          <IconButton
                            onClick={() => setEndDate(null)}
                            disabled={!endDate}
                            aria-label={t(
                              "PerformanceProFDAExportPage.clearEndDate",
                            )}
                            aria-hidden={!endDate}
                            tabIndex={endDate ? 0 : -1}
                            style={{
                              opacity: endDate ? 1 : 0,
                              pointerEvents: endDate ? "auto" : "none",
                              padding: 6,
                              marginRight: -6,
                            }}
                          >
                            <ClearIcon color="disabled" fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    InputAdornmentProps={{
                      position: "end",
                      style: { order: 2, marginLeft: -2 },
                    }}
                    TextFieldComponent={FullWidthTextField}
                  />
                </MuiPickersUtilsProvider>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                disabled={!startDate || !endDate || !!supply}
                className={classes.productSelectAutocomplete}
                onChange={(_, newValue) => setProduct(newValue)}
                options={products}
                getOptionLabel={(option: any) => option}
                getOptionSelected={(option: any, value: any) => {
                  return option.code === value.code;
                }}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    label={t("PerformanceProFDAExportPage.product")}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                )}
              />
              <p className={classes.orText}>OR</p>
              <Autocomplete
                disabled={!startDate || !endDate || !!product}
                className={classes.supplySelectAutocomplete}
                onChange={(_, value) => {
                  setSupply(value);
                }}
                options={supplies}
                getOptionLabel={(option: any) => option}
                getOptionSelected={(option: any, value: any) => {
                  return option.code === value.code;
                }}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    label={t("PerformanceProFDAExportPage.supply")}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                )}
              />
            </Grid>
          </Grid>
        </Paper>
      </div>
      <Box display="flex" justifyContent="center" alignItems="center">
        <Button
          data-testid="downloadFdaExportButton"
          variant="contained"
          size="small"
          onClick={handleDownload}
          className={classes.downloadButton}
          startIcon={<DownloadIcon />}
          disabled={
            disableDownloadButton(startDate, endDate, product, supply) ||
            isPageLoading
          }
        >
          Download
        </Button>
      </Box>
    </>
  );
}
